<template>
  <div>
    <div class="row">
      <div class="col-12">
        <div class="nature-white-box">
          <div v-if="generating" class="alert alert-info text-center py-2 mb-2">
            <div
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></div>
            {{ $t("manufacturingGrant.generating") }}
          </div>
          <div
            v-if="errorMessage"
            class="alert alert-danger text-center py-2 mb-2"
          >
            <span>{{ errorMessage }}</span>
            <button
              class="btn btn-sm btn-outline-danger ms-3"
              @click="loadData"
            >
              {{ $t("manufacturingGrant.retry") }}
            </button>
          </div>
          <div
            v-if="stats.total_operations > 0"
            class="stats-bar row mx-0 mb-3 px-3 py-2 bg-light rounded"
          >
            <div class="col" v-if="list.length > 0">
              <button
                class="btn btn-sm btn-no-r"
                :class="viewType === 1 ? 'btn-primary' : 'btn-secondary'"
                @click="viewType = 1"
              >
                {{ $t("manufacturingGrant.userView") }}
              </button>
              <button
                class="btn btn-sm btn-no-r btn-no-l"
                :class="viewType === 2 ? 'btn-primary' : 'btn-secondary'"
                @click="viewType = 2"
              >
                {{ $t("manufacturingGrant.machineView") }}
              </button>
              <button
                class="btn btn-sm btn-no-l"
                :class="viewType === 0 ? 'btn-primary' : 'btn-secondary'"
                @click="viewType = 0"
              >
                {{ $t("manufacturingGrant.productView") }}
              </button>
            </div>
            <div class="col-auto stats-item">
              <span class="stats-label"
                >{{ $t("manufacturingGrant.totalItems") }}:</span
              >
              <span class="stats-value">{{ stats.total_items }}</span>
            </div>
            <div class="col-auto stats-item">
              <span class="stats-label"
                >{{ $t("manufacturingGrant.totalOperations") }}:</span
              >
              <span class="stats-value">{{ stats.total_operations }}</span>
            </div>
            <div class="col-auto stats-item">
              <span class="stats-label"
                >{{ $t("manufacturingGrant.totalWorkers") }}:</span
              >
              <span class="stats-value">{{ stats.total_workers }}</span>
            </div>
            <div class="col-auto stats-item" v-if="stats.date_start">
              <span class="stats-label"
                >{{ $t("manufacturingGrant.dateRange") }}:</span
              >
              <span class="stats-value"
                >{{ formatDate(stats.date_start) }} &ndash;
                {{ formatDate(stats.date_end) }}</span
              >
            </div>
            <div class="col-auto ms-auto d-flex align-items-center gap-2">
              <span class="stats-label"
                >{{ $t("manufacturingGrant.zoom") }}:</span
              >
              <input
                type="range"
                class="zoom-slider"
                min="100"
                max="6000"
                step="50"
                v-model.number="dayWith"
              />
              <span class="stats-value zoom-value">{{ dayWith }}px</span>
            </div>
            <div class="col-auto">
              <button
                class="btn btn-sm btn-outline-primary"
                @click="showGenerate"
              >
                {{ $t("manufacturingGrant.generateSchedule") }}
              </button>
            </div>
          </div>

          <!-- Generate Schedule Options Modal -->
          <div
            ref="generateModal"
            class="modal fade"
            tabindex="-1"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg modal-dialog-centered">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">
                    {{ $t("manufacturingGrant.generateSchedule") }}
                  </h5>
                  <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div class="modal-body">
                  <div class="mb-3 custom-input">
                    <label class="form-label bold-12 text-gray">{{
                      $t("manufacturingGrant.policy")
                    }}</label>
                    <div class="glass-radio-group">
                      <input
                        type="radio"
                        name="policy"
                        id="policyUIS"
                        value="UIS"
                        v-model="generateOptions.policy"
                      />
                      <label class="glass-radio" for="policyUIS">{{
                        $t("manufacturingGrant.policyUISLabel")
                      }}</label>
                      <input
                        type="radio"
                        name="policy"
                        id="policyNIS"
                        value="NIS"
                        v-model="generateOptions.policy"
                      />
                      <label class="glass-radio" for="policyNIS">{{
                        $t("manufacturingGrant.policyNISLabel")
                      }}</label>
                    </div>
                    <div class="mt-2 text-muted-8 small italic-text">
                      {{
                        generateOptions.policy === "UIS"
                          ? $t("manufacturingGrant.policyUISHelp")
                          : $t("manufacturingGrant.policyNISHelp")
                      }}
                    </div>
                  </div>
                  <div class="row mb-3 custom-input">
                    <div class="col-4">
                      <label class="form-label bold-12 text-gray"
                        >{{
                          $t("manufacturingGrant.shiftStart")
                        }}
                        (HH:mm)</label
                      >
                      <input
                        type="time"
                        class="form-control"
                        v-model="generateOptions.shift_start_time"
                      />
                    </div>
                    <div class="col-4">
                      <label class="form-label bold-12 text-gray"
                        >{{ $t("manufacturingGrant.shiftLength") }} ({{
                          $t("manufacturingGrant.hours")
                        }})</label
                      >
                      <input
                        type="number"
                        class="form-control"
                        v-model="generateOptions.shift_length_hours"
                      />
                    </div>
                    <div class="col-4">
                      <label class="form-label bold-12 text-gray"
                        >{{ $t("manufacturingGrant.breakLength") }} ({{
                          $t("manufacturingGrant.hours")
                        }})</label
                      >
                      <input
                        type="number"
                        class="form-control"
                        v-model="generateOptions.break_length_hours"
                      />
                    </div>
                  </div>
                  <div class="mb-3 custom-input">
                    <label class="form-label bold-12 text-gray">{{
                      $t("manufacturingGrant.schedulingMode")
                    }}</label>
                    <div class="glass-radio-group">
                      <input
                        type="radio"
                        name="mode"
                        id="mode1"
                        value="REGENERATE_ALL"
                        v-model="generateOptions.mode"
                      />
                      <label class="glass-radio" for="mode1">
                        {{ $t("manufacturingGrant.modeRegenerateAll") }}
                      </label>
                      <input
                        type="radio"
                        name="mode"
                        id="mode2"
                        value="ADD_NEW"
                        v-model="generateOptions.mode"
                      />
                      <label class="glass-radio" for="mode2">
                        {{ $t("manufacturingGrant.modeAddNew") }}
                      </label>
                      <input
                        type="radio"
                        name="mode"
                        id="mode3"
                        value="FREEZE_WINDOW"
                        v-model="generateOptions.mode"
                      />
                      <label class="glass-radio" for="mode3">
                        {{ $t("manufacturingGrant.modeFreezeWindow") }}
                      </label>
                    </div>
                    <div class="mt-2 text-muted-8 small italic-text">
                      <template
                        v-if="generateOptions.mode === 'REGENERATE_ALL'"
                      >
                        {{ $t("manufacturingGrant.modeRegenerateAllHelp") }}
                      </template>
                      <template v-else-if="generateOptions.mode === 'ADD_NEW'">
                        {{ $t("manufacturingGrant.modeAddNewHelp") }}
                      </template>
                      <template
                        v-else-if="generateOptions.mode === 'FREEZE_WINDOW'"
                      >
                        {{ $t("manufacturingGrant.modeFreezeWindowHelp") }}
                      </template>
                    </div>
                  </div>
                  <div class="mb-3 custom-input">
                    <label class="form-label bold-12 text-gray">{{
                      $t("manufacturingGrant.groupVariantsThreshold")
                    }}</label>
                    <div class="fix-end-input">
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="
                          generateOptions.group_variants_threshold
                        "
                        min="0"
                        placeholder="0"
                      />
                      <span>{{ $t("manufacturingGrant.minutes") }}</span>
                    </div>
                    <div class="mt-1 text-muted-8 small italic-text">
                      {{ $t("manufacturingGrant.groupVariantsThresholdHelp") }}
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    {{ $t("manufacturingGrant.cancel") }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="confirmGenerate"
                  >
                    {{ $t("manufacturingGrant.start") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="row overflow-hidden mt-4">
            <div
              class="timeline-container w-100 dragscroll fix-height"
              ref="timelineContainer"
            >
              <div v-if="loading" class="p-5 text-center w-100">
                <div
                  class="spinner-border text-primary mb-3"
                  role="status"
                ></div>
                <h5>{{ $t("manufacturingGrant.loading") }}</h5>
              </div>
              <div v-else-if="list.length === 0" class="p-5 text-center w-100">
                <h5>{{ $t("manufacturingGrant.noData") }}</h5>
                <p>{{ $t("manufacturingGrant.noDataHint") }}</p>
              </div>
              <div v-else class="timeline">
                <div class="timeline-head" ref="timelineHead">
                  <div class="timeline-title"></div>
                  <div
                    class="timeline-month"
                    :class="[
                      yearChange(i) ? 'year' : '',
                      isWeekend(date) ? 'weekend' : '',
                    ]"
                    :style="'width:' + date.width + 'px'"
                    v-for="(date, i) in dayWidths"
                    :key="date.i"
                  >
                    <div class="month-label">
                      <div class="op-dot" v-if="date.hasOps"></div>
                      {{ date.weekdayName }}
                    </div>
                    <div class="day-row">
                      <span class="day-label">{{ date.day }}</span>
                      <span class="weekday-label">{{ date.monthName }}</span>
                    </div>
                  </div>
                </div>
                <div class="timeline-body">
                  <div
                    class="timeline-item"
                    v-for="service in list"
                    :key="service.id"
                  >
                    <div class="timeline-title d-grid">
                      <div
                        class="tl-title-accent"
                        :class="'accent-' + (service.type || '').toLowerCase()"
                      ></div>
                      <div class="tl-title-body">
                        <div class="tl-title-row">
                          <span
                            class="
                              d-inline-flex
                              align-items-center
                              justify-content-center
                              rounded-circle
                              text-white
                              semibold-12
                              flex-shrink-0
                            "
                            :style="{
                              width: '28px',
                              height: '28px',
                              background:
                                service.type === 'Worker'
                                  ? 'var(--default)'
                                  : service.type === 'Machine'
                                  ? 'var(--lyellow)'
                                  : '#4a6cf7',
                            }"
                            >{{ (service.type || "")[0] }}</span
                          >
                          <span class="timeline-name">{{ service.name }}</span>
                        </div>
                        <div class="tl-title-row tl-title-meta">
                          <span class="tl-serial-label">{{
                            service.secondary_label
                          }}</span>
                          <span
                            class="tl-char-pills"
                            v-if="
                              viewType === 0 &&
                              service.times?.[0]?.characteristics?.length
                            "
                          >
                            <span
                              class="tl-char-pill"
                              v-for="c in service.times[0].characteristics"
                              :key="c.type_id"
                              >{{ c.value }}</span
                            >
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="timeline-row">
                      <div
                        class="timeline-line"
                        v-for="line in service.times"
                        :key="line.id"
                        :style="{
                          left: timeToX(line.start_time) + 'px',
                          width: barWidth(line) + 'px',
                          'border-color': line.color || '#90daf0',
                        }"
                        :class="[
                          barSizeClass(line),
                          { highlighted: isHighlighted(line) },
                        ]"
                        @click="showDetail(line)"
                      >
                        <div class="tl-bar">
                          <span class="tl-name">{{ line.name }}</span>
                          <span
                            class="tl-serial"
                            v-if="line?.product?.serial"
                            >{{ line.product.serial }}</span
                          >
                          <span
                            class="tl-qty"
                            v-if="
                              line.quantity != null &&
                              line.quantity !== line.itemQuantity
                            "
                            >&times;{{ line.quantity }}</span
                          >
                          <span
                            class="tl-chars"
                            v-if="line.characteristics?.length"
                          >
                            <span
                              class="tl-char"
                              v-for="c in line.characteristics"
                              :key="c.type_id"
                              >{{ c.value }}</span
                            >
                          </span>
                        </div>
                        <span class="tl-end">{{
                          durationLabel(line.start_time, line.end_time)
                        }}</span>
                        <div
                          class="timeline-color"
                          :style="{ background: line.color || '#90daf0' }"
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="now-line"
                    :style="{ left: nowLinePos + 'px' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="detailModal" class="modal fade" tabindex="-1" aria-hidden="true">
      <div
        class="
          modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable
        "
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title bold-19">
              <span class="text-gradient me-2"
                >#{{
                  selectedSchedule?.product_manufacturing_operation
                    ?.manufacturingOperation?.id ||
                  selectedSchedule?.id ||
                  ""
                }}</span
              >
              {{
                selectedLine?.name || $t("manufacturingGrant.operationDetails")
              }}
            </h5>
            <div class="d-flex align-items-center gap-3">
              <div class="text-end" v-if="selectedLine">
                <div class="regular-10 text-muted">
                  {{ $t("manufacturingGrant.startTime") }} /
                  {{ $t("manufacturingGrant.endTime") }}
                </div>
                <div class="semibold-12">
                  {{
                    selectedLine ? formatDateTime(selectedLine.start_time) : "–"
                  }}
                  &ndash;
                  {{
                    selectedLine ? formatDateTime(selectedLine.end_time) : "–"
                  }}
                </div>
              </div>
              <button class="btn-close" data-bs-dismiss="modal"></button>
            </div>
          </div>
          <div v-if="!selectedLine" class="modal-body text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <h5 class="bold-16 text-muted">
              {{ $t("manufacturingGrant.loading") }}
            </h5>
          </div>
          <div
            v-else
            class="modal-body"
            style="max-height: 65vh; overflow: hidden"
          >
            <div class="row g-4 h-100">
              <!-- LEFT: Product info -->
              <div class="col-3">
                <div class="bold-12 text-muted mb-2">
                  {{ $t("manufacturingGrant.product") }}
                </div>
                <div
                  class="white-box p-0 mb-3"
                  style="
                    aspect-ratio: 1;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--extra-light);
                  "
                >
                  <img
                    v-if="productImageUrl"
                    :src="productImageUrl"
                    alt=""
                    style="width: 100%; height: 100%; object-fit: contain"
                  />
                  <span v-else class="text-muted" style="font-size: 3rem"
                    >👗</span
                  >
                </div>
                <div class="bold-16">
                  {{
                    selectedLine.product?.product_translations?.[0]?.name ||
                    selectedLine.product?.serial ||
                    "–"
                  }}
                </div>
                <div class="regular-12 text-muted mt-1">
                  {{ selectedLine.product?.serial || "" }}
                </div>

                <hr class="my-3" />
                <div class="bold-12 text-muted mb-2">
                  {{ $t("manufacturingGrant.team") }}
                </div>
                <div
                  v-if="selectedLine.worker"
                  class="d-flex align-items-center gap-2 p-2 mb-2 rounded"
                  style="background: var(--bg)"
                >
                  <span
                    class="
                      d-inline-flex
                      align-items-center
                      justify-content-center
                      rounded-circle
                      text-white
                      semibold-12
                    "
                    style="
                      width: 32px;
                      height: 32px;
                      background: var(--default);
                      flex-shrink: 0;
                    "
                    >{{ (selectedLine.worker || "").charAt(0) }}</span
                  >
                  <span class="semibold-14">{{ selectedLine.worker }}</span>
                </div>
                <div v-else class="regular-12 text-muted mb-2">–</div>

                <div
                  v-if="selectedLine.machine"
                  class="bold-12 text-muted mb-2"
                >
                  {{ $t("manufacturingGrant.machine") }}
                </div>
                <div
                  v-if="selectedLine.machine"
                  class="d-flex align-items-center gap-2 p-2 rounded"
                  style="background: var(--bg)"
                >
                  <span
                    class="
                      d-inline-flex
                      align-items-center
                      justify-content-center
                      rounded-circle
                    "
                    style="
                      width: 32px;
                      height: 32px;
                      background: var(--lyellow);
                      flex-shrink: 0;
                    "
                    >🔧</span
                  >
                  <span class="semibold-14">{{ selectedLine.machine }}</span>
                </div>
              </div>

              <!-- MIDDLE: Operation details -->
              <div class="col-5">
                <div class="d-flex align-items-baseline gap-2 mb-2">
                  <span class="bold-24" style="color: var(--default)"
                    >#{{
                      selectedSchedule?.product_manufacturing_operation
                        ?.manufacturingOperation?.id ||
                      selectedSchedule?.id ||
                      ""
                    }}</span
                  >
                  <span class="bold-19">{{ selectedLine.name }}</span>
                </div>
                <div
                  v-if="
                    selectedSchedule?.product_manufacturing_operation
                      ?.description
                  "
                  class="regular-14 text-muted mb-3"
                >
                  {{
                    selectedSchedule.product_manufacturing_operation.description
                  }}
                </div>

                <div class="row g-2 mb-3">
                  <div class="col-4">
                    <div
                      class="rounded p-3 text-center"
                      style="background: var(--default-04)"
                    >
                      <div class="bold-24" style="color: var(--default)">
                        {{
                          selectedSchedule?.product_manufacturing_operation
                            ?.time
                            ? selectedSchedule.product_manufacturing_operation
                                .time + "s"
                            : "–"
                        }}
                      </div>
                      <div class="bold-11 text-muted mt-1">
                        {{ $t("manufacturingGrant.unitTime") }}
                      </div>
                    </div>
                  </div>
                  <div class="col-4">
                    <div
                      class="rounded p-3 text-center"
                      style="background: #f0f0ff"
                    >
                      <div class="bold-24" style="color: var(--default)">
                        {{
                          selectedSchedule?.quantity ??
                          selectedSchedule?.item_operation?.quantity ??
                          "–"
                        }}
                      </div>
                      <div class="bold-11 text-muted mt-1">
                        {{ $t("manufacturingGrant.quantity") }}
                      </div>
                    </div>
                  </div>
                  <div class="col-4">
                    <div
                      class="rounded p-3 text-center"
                      style="background: var(--lgreen)"
                    >
                      <div class="bold-24" style="color: var(--green)">
                        {{ totalOpHours }}
                      </div>
                      <div class="bold-11 text-muted mt-1">
                        {{ $t("manufacturingGrant.totalTime") }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="bold-12 text-muted mb-2">
                  {{ $t("manufacturingGrant.characteristics") }}
                </div>
                <div
                  v-if="selectedLine.characteristics?.length"
                  class="d-flex flex-column gap-1"
                >
                  <div
                    v-for="c in selectedLine.characteristics"
                    :key="c.type_id"
                    class="
                      d-flex
                      justify-content-between
                      align-items-center
                      gap-3
                      p-2
                      rounded
                    "
                    style="background: var(--bg)"
                  >
                    <span class="semibold-12 text-muted">{{
                      c.name || c.type_id
                    }}</span>
                    <span class="semibold-14">{{ c.value }}</span>
                  </div>
                </div>
                <div v-else class="regular-12 text-muted">–</div>

                <hr class="my-3" v-if="siblingVariants.length" />
                <div
                  v-if="siblingVariants.length"
                  class="bold-12 text-muted mb-2"
                >
                  {{ $t("manufacturingGrant.otherVariants") }}
                </div>
                <div
                  v-if="siblingVariants.length"
                  class="d-flex flex-column gap-1"
                >
                  <div
                    v-for="sv in siblingVariants"
                    :key="sv.variantId"
                    class="
                      d-flex
                      align-items-start
                      gap-2
                      p-2
                      rounded
                      cursor-pointer
                    "
                    :style="{
                      background: sv.isCurrent
                        ? 'var(--default-04)'
                        : 'var(--bg)',
                      border: sv.isCurrent
                        ? '1px solid var(--default)'
                        : 'none',
                    }"
                    @click="
                      !sv.isCurrent && showDetailBySchedule(sv.scheduleItem)
                    "
                  >
                    <div class="flex-fill">
                      <div
                        class="semibold-12"
                        :class="sv.isCurrent ? 'text-primary' : ''"
                      >
                        {{ sv.product?.serial || "#" + sv.variantId }}
                      </div>
                      <div
                        v-if="sv.characteristics?.length"
                        class="regular-10 text-muted mt-1"
                      >
                        <span
                          v-for="(c, ci) in sv.characteristics"
                          :key="c.type_id"
                        >
                          {{ c.value
                          }}<span v-if="ci < sv.characteristics.length - 1"
                            >,
                          </span>
                        </span>
                      </div>
                      <div class="d-flex gap-3 mt-1">
                        <span class="regular-10 text-muted"
                          >{{ $t("manufacturingGrant.quantity") }}:
                          {{ sv.quantity }}</span
                        >
                        <span class="regular-10 text-muted"
                          >{{ $t("manufacturingGrant.totalTime") }}:
                          {{ sv.totalTime }}</span
                        >
                      </div>
                    </div>
                    <span
                      v-if="!sv.isCurrent"
                      class="text-primary semibold-12 text-nowrap"
                      style="font-size: 0.7rem; margin-top: 2px"
                      >{{ $t("manufacturingGrant.view") }}</span
                    >
                    <span
                      v-else
                      class="semibold-12 text-nowrap"
                      style="
                        font-size: 0.7rem;
                        margin-top: 2px;
                        color: var(--default);
                      "
                      >{{ $t("manufacturingGrant.current") }}</span
                    >
                  </div>
                </div>
              </div>

              <!-- RIGHT: Operations block with +/- done -->
              <div
                class="col-4"
                style="
                  max-height: calc(65vh - 2rem);
                  overflow-y: auto;
                  overflow-x: hidden;
                "
              >
                <div
                  class="d-flex align-items-center justify-content-between mb-2"
                >
                  <div class="bold-12 text-muted">
                    {{ $t("manufacturingGrant.operations") }}
                  </div>
                  <button
                    class="btn btn-sm btn-outline-primary"
                    @click="saveOpsDone"
                    :disabled="modalSaving"
                    v-if="hasChanges"
                  >
                    <span
                      v-if="modalSaving"
                      class="spinner-border spinner-border-sm me-1"
                    ></span>
                    {{ $t("manufacturingGrant.save") }}
                  </button>
                </div>
                <div v-if="isLoadingOps" class="text-center py-3">
                  <div
                    class="spinner-border spinner-border-sm text-primary"
                  ></div>
                </div>
                <div
                  v-else-if="modalSteps.length"
                  class="d-flex flex-column gap-2"
                >
                  <div
                    v-for="(step, si) in modalSteps"
                    :key="step.id"
                    class="rounded p-2 cursor-pointer"
                    :class="[
                      step.id === currentOpId ? 'step-active' : 'step-item',
                    ]"
                    @click="goToStep(step)"
                  >
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <span
                        class="bold-12 text-muted"
                        style="min-width: 24px"
                        >{{ ordinalSuffix(step.order) }}</span
                      >
                      <span
                        class="semibold-12 flex-fill"
                        :class="step.id === currentOpId ? 'text-white' : ''"
                        >{{ step.name }}</span
                      >
                      <span
                        class="semibold-12 text-nowrap"
                        :class="
                          step.id === currentOpId
                            ? 'text-white-50'
                            : 'text-muted'
                        "
                        style="font-size: 0.7rem"
                        >{{
                          step.time
                            ? Math.round((step.time * modalTotal) / 60) + "m"
                            : ""
                        }}</span
                      >
                    </div>
                    <div
                      v-if="step.description"
                      class="regular-10 mb-1"
                      :class="
                        step.id === currentOpId ? 'text-white-50' : 'text-muted'
                      "
                      style="line-height: 1.3"
                    >
                      {{ step.description }}
                    </div>
                    <div class="d-flex align-items-center gap-2 mt-1">
                      <span
                        class="semibold-12"
                        :class="step.id === currentOpId ? 'text-white' : ''"
                        >{{ $t("manufacturingGrant.done") }}:
                        {{ doneVal(step.id) }}/{{ modalTotal }}</span
                      >
                      <div class="d-flex gap-1 ms-auto" @click.stop>
                        <button
                          class="btn btn-sm"
                          :class="
                            step.id === currentOpId
                              ? 'btn-light'
                              : 'btn-outline-primary'
                          "
                          @click="addNumber(step.id, 1)"
                          :disabled="
                            doneVal(step.id) >= modalTotal ||
                            (si > 0 &&
                              doneVal(step.id) >=
                                doneVal(modalSteps[si - 1].id))
                          "
                          style="
                            padding: 0 6px;
                            font-size: 0.75rem;
                            line-height: 1.5;
                          "
                        >
                          +1
                        </button>
                        <button
                          class="btn btn-sm"
                          :class="
                            step.id === currentOpId
                              ? 'btn-light'
                              : 'btn-outline-primary'
                          "
                          @click="addNumber(step.id, 5)"
                          :disabled="
                            doneVal(step.id) >= modalTotal ||
                            (si > 0 &&
                              doneVal(step.id) >=
                                doneVal(modalSteps[si - 1].id))
                          "
                          style="
                            padding: 0 6px;
                            font-size: 0.75rem;
                            line-height: 1.5;
                          "
                        >
                          +5
                        </button>
                        <button
                          class="btn btn-sm"
                          :class="
                            step.id === currentOpId
                              ? 'btn-light'
                              : 'btn-outline-primary'
                          "
                          @click="addNumber(step.id, addMaxI(si))"
                          :disabled="!showAddBtn(si)"
                          style="
                            padding: 0 6px;
                            font-size: 0.75rem;
                            line-height: 1.5;
                          "
                        >
                          +{{ addMaxI(si) }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="regular-12 text-muted py-3 text-center">
                  {{ $t("manufacturingGrant.noOperations") }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import { mapState } from "vuex";
import http from "../../modules/http";
import ws from "../../modules/ws";
import tenant from "../../modules/tenant";

const bootstrap = require("bootstrap");

export default {
  name: "ManufacturingGrant",
  data() {
    return {
      loading: true,
      generating: false,
      errorMessage: "",
      dayWith: 2100,
      viewType: 1,
      stats: {
        total_operations: 0,
        total_items: 0,
        total_machines: 0,
        total_workers: 0,
        date_start: null,
        date_end: null,
      },
      methods: [
        {
          title: this.$t
            ? this.$t("manufacturingGrant.generateSchedule")
            : "Generate Schedule",
          icon: "play-circle",
          always: true,
          method: () => {
            if (!this.generateModal) {
              this.generateModal = new bootstrap.Modal(
                this.$refs.generateModal
              );
            }
            this.generateModal.show();
          },
        },
        {
          title: this.$t
            ? this.$t("manufacturingGrant.productView")
            : "Product view",
          icon: "box",
          always: true,
          method: () => {
            this.viewType = 0;
          },
        },
        {
          title: this.$t ? this.$t("manufacturingGrant.userView") : "User view",
          icon: "partners",
          always: true,
          method: () => {
            this.viewType = 1;
          },
        },
        {
          title: this.$t
            ? this.$t("manufacturingGrant.machineView")
            : "Machine view",
          icon: "scissor",
          always: true,
          method: () => {
            this.viewType = 2;
          },
        },
      ],
      services: [],
      users: [],
      machines: [],
      selectedLine: null,
      selectedSchedule: null,
      detailModal: null,
      generateModal: null,
      modalSteps: [],
      modalDone: {},
      modalDoneNow: {},
      modalTotal: 0,
      modalItemId: null,
      modalWarehouseOpId: null,
      modalSaving: false,
      isLoadingOps: false,
      generateOptions: {
        mode: "REGENERATE_ALL",
        policy: "UIS",
        shift_start_time: "09:00",
        shift_length_hours: 8,
        break_length_hours: 16,
        group_variants_threshold: 0,
      },
      now: Date.now(),
    };
  },
  computed: {
    ...mapState({
      searchQuery: (state) => state.topBar?.q || "",
    }),
    list() {
      let sourceData = this.services;

      if (this.viewType === 1) {
        sourceData = this.users.length > 0 ? this.users : this.services;
      } else if (this.viewType === 2) {
        sourceData = this.machines.length > 0 ? this.machines : this.services;
      }

      if (this.services.length > 0) {
        if (this.viewType === 1) {
          const userMap = {};
          this.services.forEach((s) => {
            const worker = s.worker;
            if (!worker) return;
            if (!userMap[worker.id]) {
              userMap[worker.id] = {
                id: worker.id,
                name: worker.name,
                type: "Worker",
                secondary_label: "",
                times: [],
              };
            }
            userMap[worker.id].times.push(this.mapScheduleToTime(s));
          });
          return Object.values(userMap).filter((s) => s.times?.length > 0);
        } else if (this.viewType === 2) {
          const machineMap = {};
          this.services.forEach((s) => {
            const asset = s.asset;
            if (!asset) return;
            if (!machineMap[asset.id]) {
              machineMap[asset.id] = {
                id: asset.id,
                name: asset.name,
                type: "Machine",
                secondary_label: asset.model,
                times: [],
              };
            }
            machineMap[asset.id].times.push(this.mapScheduleToTime(s));
          });
          return Object.values(machineMap).filter((s) => s.times?.length > 0);
        } else if (this.viewType === 0 && !this.services[0].times) {
          const productMap = {};
          this.services.forEach((s) => {
            const variant = s.item_operation?.item?.productVariant;
            const product = variant?.product;
            if (!product) return;
            const productName =
              product.serial ||
              product.product_translations?.[0]?.name ||
              "Product";
            if (!productMap[variant.id]) {
              productMap[variant.id] = {
                id: variant.id,
                name: productName,
                type: "Product",
                secondary_label: product.serial,
                times: [],
              };
            }
            productMap[variant.id].times.push(this.mapScheduleToTime(s));
          });
          return Object.values(productMap).filter((s) => s.times?.length > 0);
        }
      }

      return Array.isArray(sourceData)
        ? sourceData.filter((s) => s.times?.length > 0)
        : [];
    },
    viewLabel() {
      const labels = [
        this.$t("manufacturingGrant.productView"),
        this.$t("manufacturingGrant.userView"),
        this.$t("manufacturingGrant.machineView"),
      ];
      return labels[this.viewType] || labels[0];
    },
    totalOpHours() {
      if (!this.selectedSchedule) return "–";
      const timeSec =
        this.selectedSchedule.product_manufacturing_operation?.time || 0;
      const qty =
        this.selectedSchedule?.quantity ??
        this.selectedSchedule.item_operation?.quantity ??
        0;
      if (!timeSec || !qty) return "–";
      return ((timeSec * qty) / 3600).toFixed(1) + "h";
    },
    currentOpId() {
      return this.selectedSchedule?.product_manufacturing_operation?.id || null;
    },
    hasChanges() {
      for (const id in this.modalDoneNow) {
        if (this.modalDoneNow[id] > 0) return true;
      }
      return false;
    },
    productImageUrl() {
      const product = this.selectedLine?.product;
      if (!product) return null;
      const images = product.images;
      if (!images?.length) return null;
      const img =
        images.find((i) => i.pivot?.in_manufacturing) ||
        images.find((i) => i.pivot?.main) ||
        images[0];
      if (!img?.path) return null;
      const name = img.name || "";
      return (
        (this.mediaServer || "") + "/" + img.path + "/262-" + name + ".webp"
      );
    },
    siblingVariants() {
      const s = this.selectedSchedule;
      if (!s?.item_operation?.item?.productVariant) return [];
      const curVariantId = s.item_operation.item.productVariant.id;
      const curProductId = s.item_operation.item.productVariant.product?.id;
      const curPmoId = s.product_manufacturing_operation?.id;
      if (!curProductId) return [];
      const all = [];
      const curQty = s.item_operation?.quantity || 0;
      const curTime = s.product_manufacturing_operation?.time || 0;
      all.push({
        variantId: curVariantId,
        product: s.item_operation.item.productVariant.product,
        scheduleItem: s,
        characteristics: s.characteristics || [],
        quantity: curQty,
        totalTime:
          curTime && curQty
            ? ((curTime * curQty) / 3600).toFixed(1) + "h"
            : "–",
        isCurrent: true,
      });
      const seen = new Map();
      (this.services || []).forEach((item) => {
        const v = item.item_operation?.item?.productVariant;
        if (!v || v.id === curVariantId) return;
        if (v.product?.id !== curProductId) return;
        if (item.product_manufacturing_operation?.id !== curPmoId) return;
        if (!seen.has(v.id)) {
          const time = item.product_manufacturing_operation?.time || 0;
          const qty = item.item_operation?.quantity || 0;
          seen.set(v.id, {
            variantId: v.id,
            product: v.product,
            scheduleItem: item,
            characteristics: item.characteristics || [],
            quantity: qty,
            totalTime:
              time && qty ? ((time * qty) / 3600).toFixed(1) + "h" : "–",
            isCurrent: false,
          });
        }
      });
      return all
        .concat(Array.from(seen.values()))
        .sort((a, b) => a.variantId - b.variantId);
    },
    mediaServer() {
      return tenant.getAppMediaUrl();
    },
    nowLinePos() {
      if (!this.dayWidths.length) return 0;
      return 220 + this.timeToX(this.now);
    },
    dayWidths() {
      const occupiedDays = new Set();
      const dayBounds = {};
      this.services.forEach((s) => {
        const startDay = dayjs(s.start_time).startOf("day").valueOf();
        const endDay = dayjs(s.end_time).startOf("day").valueOf();
        const startSec = dayjs(s.start_time).diff(
          dayjs(s.start_time).startOf("day"),
          "second"
        );
        const endSec = dayjs(s.end_time).diff(
          dayjs(s.end_time).startOf("day"),
          "second"
        );
        for (let d = startDay; d <= endDay; d += 86400000) {
          occupiedDays.add(d);
          if (!dayBounds[d])
            dayBounds[d] = { first: Infinity, last: -Infinity };
          if (d === startDay && d === endDay) {
            dayBounds[d].first = Math.min(dayBounds[d].first, startSec);
            dayBounds[d].last = Math.max(dayBounds[d].last, endSec);
          } else if (d === startDay) {
            dayBounds[d].first = Math.min(dayBounds[d].first, startSec);
            dayBounds[d].last = 86400;
          } else if (d === endDay) {
            dayBounds[d].first = 0;
            dayBounds[d].last = Math.max(dayBounds[d].last, endSec);
          } else {
            dayBounds[d].first = 0;
            dayBounds[d].last = 86400;
          }
        }
      });
      const result = [];
      let offset = 0;
      let minStart = Infinity;
      this.services.forEach((s) => {
        const t = dayjs(s.start_time).valueOf();
        if (t < minStart) minStart = t;
      });
      const earliest =
        minStart < Infinity
          ? dayjs(minStart).startOf("day")
          : dayjs().startOf("day");
      const now = dayjs().startOf("day");
      const start = earliest.isBefore(now) ? earliest : now;
      const dayCount = Math.max(
        60,
        Math.ceil(dayjs().diff(start, "day", true)) + 30
      );
      for (let i = 0; i < dayCount; i++) {
        const d = start.add(i, "day");
        const ts = d.valueOf();
        const hasOps = occupiedDays.has(ts);
        let width;
        if (!hasOps) {
          width = 300;
        } else {
          const b = dayBounds[ts];
          const opsWindow = b.last - b.first;
          const opsWidth = (opsWindow / 86400) * this.dayWith;
          width = 200 + opsWidth + 200;
        }
        result.push({
          day: d.date(),
          year: d.year(),
          month: d.month(),
          monthName: d.format("MMMM"),
          weekday: d.day(),
          weekdayName: d.format("dddd"),
          i: i,
          dateObj: d,
          offset: offset,
          width: width,
          hasOps: hasOps,
          firstSec: hasOps ? dayBounds[ts].first : null,
          lastSec: hasOps ? dayBounds[ts].last : null,
        });
        offset += width;
      }
      return result;
    },
  },
  mounted() {
    this.loadData();
    ws.init();
    ws.subscribe("modify", "productionSchedule", () => {
      this.loadData();
      this.generating = false;
    });
    const slider = this.$refs.timelineContainer;
    let isDown = false;
    let startX;
    let scrollLeft;

    slider.addEventListener("mousedown", (e) => {
      isDown = true;
      slider.classList.add("active");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });
    slider.addEventListener("mouseleave", () => {
      isDown = false;
      slider.classList.remove("active");
    });
    slider.addEventListener("mouseup", () => {
      isDown = false;
      slider.classList.remove("active");
    });
    slider.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2;
      slider.scrollLeft = scrollLeft - walk;
    });
    slider.addEventListener(
      "wheel",
      (e) => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const delta = e.deltaY > 0 ? -100 : 100;
        this.dayWith = Math.max(100, Math.min(6000, this.dayWith + delta));
      },
      { passive: false }
    );
    this._nowTimer = setInterval(() => {
      this.now = Date.now();
    }, 60000);
  },
  unmounted() {
    window.removeEventListener("scroll", this.handleScroll);
    if (this._nowTimer) clearInterval(this._nowTimer);
  },
  methods: {
    loadData() {
      this.loading = true;
      this.errorMessage = "";
      http
        .fetch("/production/plan")
        .then((data) => {
          if (!data) return;
          this.generating = data.generating;
          this.stats = data.stats || this.stats;
          let schedules = data.schedule;

          if (Array.isArray(schedules)) {
            this.services = schedules;
            this.users = [];
            this.machines = [];
          } else {
            this.services = schedules.products || [];
            this.users = schedules.users || [];
            this.machines = schedules.machines || [];
          }
          this.loading = false;
          this.$nextTick(() => {
            this.scrollToToday();
          });
        })
        .catch(() => {
          this.loading = false;
          this.errorMessage = this.$t("manufacturingGrant.loadError");
        });
    },
    scrollToToday() {
      const container = this.$refs.timelineContainer;
      if (!container) return;
      const scrollTarget = this.nowLinePos - container.clientWidth / 3;
      container.scrollLeft = Math.max(0, scrollTarget);
    },
    formatDate(val) {
      if (!val) return "";
      return dayjs(val).format("MMM D, YYYY");
    },
    formatDateTime(val) {
      if (!val) return "";
      return dayjs(val).format("MMM D, YYYY HH:mm");
    },
    durationMinutes(start, end) {
      return dayjs(end).diff(dayjs(start), "minute");
    },
    durationLabel(start, end) {
      const min = dayjs(end).diff(dayjs(start), "minute");
      if (min < 60) return min + " min";
      const hours = Math.floor(min / 60);
      const mins = min % 60;
      if (mins === 0) return hours + "h";
      return hours + "h " + mins + "min";
    },
    isWeekend(date) {
      return date.weekday === 0 || date.weekday === 6;
    },
    date(val) {
      dayjs.locale("en");
      dayjs.extend(localizedFormat);
      dayjs.extend(relativeTime);
      return dayjs().to(dayjs(val));
    },
    timeToX(time) {
      const t = dayjs(time);
      if (!this.dayWidths.length) return 0;
      const ref = this.dayWidths[0].dateObj;
      const dayIndex = t.diff(ref, "day");
      if (dayIndex < 0 || dayIndex >= this.dayWidths.length) return 0;
      const dayInfo = this.dayWidths[dayIndex];
      const sec = t.diff(t.startOf("day"), "second");

      if (!dayInfo.hasOps) {
        return dayInfo.offset + (sec / 86400) * dayInfo.width;
      }

      const firstSec = dayInfo.firstSec;
      const lastSec = dayInfo.lastSec;
      const opsWindow = lastSec - firstSec;
      const opsWidth = (opsWindow / 86400) * this.dayWith;

      if (sec <= firstSec) {
        const frac = firstSec > 0 ? sec / firstSec : 0;
        return dayInfo.offset + frac * 200;
      } else if (sec >= lastSec) {
        const tail = 86400 - lastSec;
        const frac = tail > 0 ? (sec - lastSec) / tail : 0;
        return dayInfo.offset + 200 + opsWidth + frac * 200;
      } else {
        const frac = opsWindow > 0 ? (sec - firstSec) / opsWindow : 0;
        return dayInfo.offset + 200 + frac * opsWidth;
      }
    },
    barWidth(line) {
      const x1 = this.timeToX(line.start_time);
      const x2 = this.timeToX(line.end_time);
      return Math.max(10, x2 - x1);
    },
    barSizeClass(line) {
      const w = this.barWidth(line);
      if (w < 80) return "bar-xs";
      if (w < 150) return "bar-sm";
      if (w < 260) return "bar-md";
      if (w < 400) return "bar-lg";
      return "bar-xl";
    },
    isHighlighted(line) {
      if (!this.searchQuery) return false;
      const q = this.searchQuery.toLowerCase();
      const product = line?.product;
      const productName =
        product?.product_translations?.[0]?.name ||
        product?.productTranslations?.[0]?.name ||
        "";
      const customerName = line?.customer?.name || "";
      const opName = line.name || "";
      const serial = product?.serial || "";

      return (
        productName.toLowerCase().includes(q) ||
        customerName.toLowerCase().includes(q) ||
        opName.toLowerCase().includes(q) ||
        serial.toLowerCase().includes(q)
      );
    },
    yearChange(i) {
      if (i === 0) {
        return false;
      }
      return this.dayWidths[i - 1].year !== this.dayWidths[i].year;
    },
    opColor(s) {
      if (s.color) {
        return s.color.startsWith("#") || s.color.startsWith("hsl")
          ? s.color
          : "#" + s.color;
      }
      const opId =
        s.product_manufacturing_operation?.manufacturing_operation_id ||
        s.product_manufacturing_operation?.manufacturingOperation?.id ||
        s.id;
      const hue = ((opId * 137.508) % 360).toFixed(0);
      const sat = 55 + ((opId * 7) % 20);
      const lig = 85 + ((opId * 3) % 10);
      return `hsl(${hue}, ${sat}%, ${lig}%)`;
    },
    mapScheduleToTime(s) {
      return {
        id: s.id,
        name:
          s.product_manufacturing_operation?.manufacturingOperation?.name ||
          "Operation",
        start_time: s.start_time,
        end_time: s.end_time,
        color: this.opColor(s),
        product: s.item_operation?.item?.productVariant?.product,
        customer: s.item_operation?.warehouseOperation?.crmItem,
        description: s.product_manufacturing_operation?.description ?? "",
        machine: s.asset?.name,
        worker: s.worker?.name,
        characteristics: s.characteristics ?? [],
        quantity: s.quantity,
        itemQuantity: s.item_operation?.quantity ?? null,
        _raw: s,
      };
    },
    showDetail(line) {
      this.selectedLine = line;
      this.selectedSchedule = line?._raw || null;
      this.loadModalOps(this.selectedSchedule);
      if (!this.detailModal) {
        this.detailModal = new bootstrap.Modal(this.$refs.detailModal);
      }
      this.detailModal.show();
    },
    showDetailBySchedule(scheduleItem) {
      if (!scheduleItem) return;
      this.showDetail(this.mapScheduleToTime(scheduleItem));
    },
    loadModalOps(scheduleItem) {
      if (!scheduleItem) {
        this.modalSteps = [];
        this.modalDone = {};
        this.modalDoneNow = {};
        this.modalTotal = 0;
        return;
      }
      const variantId = scheduleItem.item_operation?.item?.productVariant?.id;
      const itemOp = scheduleItem.item_operation || {};
      this.modalItemId = itemOp.item_id;
      this.modalWarehouseOpId = itemOp.warehouse_operation_id;
      this.modalTotal = itemOp.quantity || scheduleItem?.quantity || 0;

      if (!variantId) {
        this.modalSteps = [];
        this.modalDone = {};
        this.modalDoneNow = {};
        return;
      }

      const seen = new Map();
      (this.services || []).forEach((s) => {
        const sv = s.item_operation?.item?.productVariant?.id;
        if (sv !== variantId) return;
        const pmo = s.product_manufacturing_operation;
        if (!pmo?.manufacturingOperation) return;
        const mo = pmo.manufacturingOperation;
        if (!seen.has(pmo.id)) {
          seen.set(pmo.id, {
            id: pmo.id,
            order: mo.order || pmo.order || 999,
            name: mo.name || "Operation",
            description: pmo.description || "",
            time: pmo.time || 0,
          });
        }
      });

      this.modalSteps = Array.from(seen.values()).sort(
        (a, b) => a.order - b.order
      );
      this.modalDone = {};
      this.modalDoneNow = {};
      this.modalSteps.forEach((s) => {
        this.modalDoneNow[s.id] = 0;
      });

      this.isLoadingOps = false;
    },
    ordinalSuffix(i) {
      if (this.$i18n && this.$i18n.locale !== "en") return i + ".";
      let j = i % 10,
        k = i % 100;
      if (j === 1 && k !== 11) return i + "st";
      if (j === 2 && k !== 12) return i + "nd";
      if (j === 3 && k !== 13) return i + "rd";
      return i + "th";
    },
    doneVal(id) {
      return (this.modalDone[id] || 0) + (this.modalDoneNow[id] || 0);
    },
    addNumber(id, number) {
      if (!number) return;
      const now = this.modalDoneNow[id] || 0;
      this.modalDoneNow = { ...this.modalDoneNow, [id]: now + number };
    },
    addMaxI(si) {
      const step = this.modalSteps[si];
      if (!step) return 0;
      if (si === 0) return Math.max(0, this.modalTotal - this.doneVal(step.id));
      const prev = this.modalSteps[si - 1];
      return Math.max(
        0,
        Math.min(
          this.modalTotal - this.doneVal(step.id),
          this.doneVal(prev.id) - this.doneVal(step.id)
        )
      );
    },
    showAddBtn(si) {
      const step = this.modalSteps[si];
      if (!step) return false;
      if (this.doneVal(step.id) >= this.modalTotal) return false;
      if (si === 0) return this.doneVal(step.id) < this.modalTotal;
      const prev = this.modalSteps[si - 1];
      return this.doneVal(step.id) < this.doneVal(prev.id);
    },
    saveOpsDone() {
      if (!this.hasChanges) return;
      this.modalSaving = true;
      const data = {
        waste: 0,
        item_id: this.modalItemId,
        operation_id: this.modalWarehouseOpId ?? this.$route.params.id,
        done: this.modalDoneNow,
      };
      http
        .fetch("/manufacturing/operations/done", data)
        .then(() => {
          for (const id in this.modalDoneNow) {
            this.modalDone[id] =
              (this.modalDone[id] || 0) + this.modalDoneNow[id];
            this.modalDoneNow[id] = 0;
          }
          this.modalSaving = false;
        })
        .catch(() => {
          this.modalSaving = false;
        });
    },
    goToStep(step) {
      const s = this.selectedSchedule;
      if (!s?.item_operation?.item?.productVariant) return;
      const variantId = s.item_operation.item.productVariant.id;
      const target = (this.services || []).find(
        (item) =>
          item.item_operation?.item?.productVariant?.id === variantId &&
          item.product_manufacturing_operation?.id === step.id
      );
      if (target) this.showDetailBySchedule(target);
    },
    showGenerate() {
      if (!this.generateModal) {
        this.generateModal = new bootstrap.Modal(this.$refs.generateModal);
      }
      this.generateModal.show();
    },
    confirmGenerate() {
      this.generating = true;
      this.errorMessage = "";
      if (this.generateModal) {
        this.generateModal.hide();
      }

      const options = {
        mode: this.generateOptions.mode,
        policy: this.generateOptions.policy,
        shift_start: this.timeToMinutes(this.generateOptions.shift_start_time),
        shift_length: this.generateOptions.shift_length_hours * 60,
        break_length: this.generateOptions.break_length_hours * 60,
        group_variants_threshold: this.generateOptions.group_variants_threshold,
      };

      http.fetch("/production/plan/generate", options).catch(() => {
        this.generating = false;
        this.errorMessage = this.$t("manufacturingGrant.scheduleError");
      });
    },
    timeToMinutes(timeStr) {
      const [h, m] = timeStr.split(":").map(Number);
      return h * 60 + m;
    },
    itemOpStatusClass(status) {
      if (status === undefined || status === null) return "bg-secondary";
      if (status <= -1) return "bg-warning text-dark";
      if (status === 0) return "bg-primary";
      if (status >= 3) return "bg-secondary";
      return "bg-info text-dark";
    },
  },
};
</script>
<style>
.timeline-container {
  height: calc(100vh - 230px);
  overflow-x: scroll;
  margin-left: calc(var(--bs-gutter-x) / -2);
  position: relative;
}
.timeline-container .timeline {
  width: max-content;
  position: relative;
  margin-left: calc(var(--bs-gutter-x) / 2);
  user-select: none;
}
.timeline-container .timeline .timeline-line {
  position: absolute;
  height: 54px;
  border-radius: 6px;
  box-sizing: border-box;
  border: 3px solid #fff;
  padding: 4px 8px;
  background: #f7f8ff;
  margin: 2px 0 0;
  box-shadow: 0 0 2px 1px rgba(202, 202, 202, 0.6);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}
.timeline-container .timeline .timeline-line:hover {
  box-shadow: 0 0 6px 2px rgba(113, 58, 232, 0.4);
  transform: translateY(-1px);
}
.timeline-container .timeline .timeline-line.highlighted {
  background: #fff9c4;
  box-shadow: 0 0 8px 2px rgba(255, 235, 59, 0.8);
  z-index: 5;
}
.tl-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 6px;
  min-width: 0;
  overflow: hidden;
  flex: 1;
  line-height: 1.35;
  max-height: 2.7em;
}
.tl-name {
  font-weight: 500;
  font-size: 0.85em;
  color: var(--gray-dark);
  overflow: hidden;
  pointer-events: none;
  min-width: 0;
}
.tl-serial {
  font-size: 0.75em;
  color: var(--gray);
  pointer-events: none;
  flex-shrink: 0;
}
.tl-qty {
  font-size: 0.75em;
  font-weight: 600;
  color: var(--default);
  pointer-events: none;
  flex-shrink: 0;
  white-space: nowrap;
}
.tl-chars {
  display: inline-flex;
  gap: 3px;
  flex-shrink: 0;
}
.tl-char {
  font-size: 0.7em;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.7);
  color: var(--gray-dark);
  pointer-events: none;
}
.tl-end {
  font-size: 0.75em;
  color: var(--gray);
  pointer-events: none;
  flex-shrink: 0;
  white-space: nowrap;
}
.timeline-container .timeline .timeline-line .timeline-color {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  pointer-events: none;
}

/* Width-adaptive visibility */
.timeline-line .tl-serial,
.timeline-line .tl-end,
.timeline-line .tl-qty {
  display: none;
}
.timeline-line.bar-sm .tl-serial {
  display: inline;
}
.timeline-line.bar-md .tl-serial {
  display: inline;
}
.timeline-line.bar-md .tl-end {
  display: inline;
}
.timeline-line.bar-md .tl-qty {
  display: inline;
}
.timeline-line.bar-lg .tl-serial {
  display: inline;
}
.timeline-line.bar-lg .tl-end {
  display: inline;
}
.timeline-line.bar-lg .tl-qty {
  display: inline;
}
.timeline-line.bar-lg .tl-chars {
  display: inline-flex;
}
.timeline-line.bar-xl .tl-serial {
  display: inline;
}
.timeline-line.bar-xl .tl-end {
  display: inline;
}
.timeline-line.bar-xl .tl-qty {
  display: inline;
}
.timeline-line.bar-xl .tl-chars {
  display: inline-flex;
}

.timeline-container .timeline .timeline-line.bar-xs {
  min-width: 12px;
  padding: 4px;
}

.timeline-row {
  position: relative;
  width: 100%;
  height: 56px;
}
.timeline-container .timeline .timeline-head {
  display: flex;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 20;
}
.timeline-container .timeline .timeline-head .timeline-title {
  position: sticky;
  left: 0;
  z-index: 25;
  background: #fff;
}
.timeline-container .timeline .timeline-head .timeline-month {
  width: 240px;
  border-left: 1px solid var(--gray);
  padding: 2px 6px;
  position: sticky;
  background: #fff;
  left: 220px;
}
.timeline-container .timeline .timeline-head .timeline-month .month-label {
  font-size: 0.6em;
  color: var(--gray);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.3;
}
.timeline-container .timeline .timeline-head .timeline-month .day-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  line-height: 1.2;
}
.timeline-container .timeline .timeline-head .timeline-month .day-label {
  font-size: 0.95em;
  font-weight: 700;
}
.timeline-container .timeline .timeline-head .timeline-month .weekday-label {
  font-size: 0.65em;
  color: var(--gray);
  text-transform: lowercase;
  font-weight: 500;
}
.timeline-container .timeline .timeline-head .timeline-month.weekend {
  background: #f5f5f5;
  border-left-color: #e0e0e0;
}
.timeline-container .timeline .timeline-head .timeline-month.weekend .day-label,
.timeline-container
  .timeline
  .timeline-head
  .timeline-month.weekend
  .weekday-label {
  color: #bdbdbd;
}
.timeline-container
  .timeline
  .timeline-head
  .timeline-month.weekend
  .day-label {
  color: #bdbdbd;
}
.timeline-container .timeline .timeline-head .timeline-month.year:before {
  content: "";
  width: 1px;
  height: 100%;
  border-left: 4px solid #713ae8;
  position: absolute;
  margin-left: -13px;
  border-radius: 6px;
}
.timeline-container .timeline .timeline-item {
  display: flex;
  gap: 0;
  margin-bottom: 0.5em;
  padding-bottom: 4px;
}
.timeline-container .timeline .timeline-title {
  width: 220px;
  z-index: 10;
  position: sticky;
  left: 0;
  background: rgb(255 255 255 / 86%);
  padding: 6px 0 6px 8px;
  margin-bottom: -4px;
  display: flex;
  gap: 8px;
  align-items: stretch;
}
.tl-title-accent {
  width: 3px;
  border-radius: 3px;
  flex-shrink: 0;
}
.tl-title-accent.accent-product {
  background: #1976d2;
}
.tl-title-accent.accent-worker {
  background: #388e3c;
}
.tl-title-accent.accent-user {
  background: #388e3c;
}
.tl-title-accent.accent-machine {
  background: #f57c00;
}
.tl-title-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.tl-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.tl-type-badge {
  font-size: 0.6em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 1px 6px;
  border-radius: 3px;
  flex-shrink: 0;
}
.tl-type-badge.type-product {
  background: #e3f2fd;
  color: #1565c0;
}
.tl-type-badge.type-worker {
  background: #e8f5e9;
  color: #2e7d32;
}
.tl-type-badge.type-user {
  background: #e8f5e9;
  color: #2e7d32;
}
.tl-type-badge.type-machine {
  background: #fff3e0;
  color: #e65100;
}
.timeline-container .timeline .timeline-name {
  font-size: 1em;
  font-weight: 600;
  color: var(--gray-dark);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tl-title-meta {
  gap: 4px 8px;
  flex-wrap: wrap;
}
.tl-serial-label {
  font-size: 0.75em;
  color: var(--gray);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tl-char-pills {
  display: inline-flex;
  gap: 3px;
  flex-wrap: wrap;
}
.tl-char-pill {
  font-size: 0.65em;
  padding: 1px 5px;
  border-radius: 3px;
  background: #f3e5f5;
  color: #7b1fa2;
}

.dragscroll {
  cursor: all-scroll;
}
.now-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e53935;
  z-index: 9;
  pointer-events: none;
}
.now-line:before {
  content: "";
  position: absolute;
  top: 0;
  left: -4px;
  width: 10px;
  height: 10px;
  background: #e53935;
  border-radius: 50%;
}
.stats-bar {
  border: 1px solid #e9ecef;
}
.stats-item {
  white-space: nowrap;
}
.stats-label {
  font-size: 0.85em;
  color: var(--gray);
  margin-right: 4px;
}
.stats-value {
  font-weight: 600;
  font-size: 0.95em;
}
.zoom-slider {
  width: 100px;
  height: 4px;
  cursor: pointer;
  accent-color: #713ae8;
}
.zoom-value {
  min-width: 50px;
  text-align: right;
}
.timeline-body {
  position: relative;
}
.current-view-badge .badge {
  font-size: 0.8em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.op-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4caf50;
  margin-left: 4px;
  vertical-align: super;
}
.characteristics-badge {
  display: inline-block;
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 0.8em;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
}
.timeline-container .timeline .timeline-item:nth-child(2n),
.timeline-container .timeline .timeline-item:nth-child(2n) .timeline-title {
  background: #f6f3ff;
  border-radius: 8px;
}

.step-item {
  background: var(--bg);
  border: 1px solid transparent;
  transition: all 0.15s;
}
.step-item:hover {
  border-color: var(--default);
}
.step-active {
  background: var(--default);
  border: 1px solid var(--default);
}
.step-active .btn-light {
  border-color: rgba(255, 255, 255, 0.3);
  color: var(--default);
}
.step-active .btn-light:hover {
  background: rgba(255, 255, 255, 0.9);
}
.text-white-50 {
  color: rgba(255, 255, 255, 0.6);
}
.cursor-pointer {
  cursor: pointer;
}
</style>
